import { useEffect, useState, useMemo } from "react";
import { api } from "@/api/client";
import { PageHeader, StatusBadge, PriorityBadge, fmtDate, EmptyState } from "@/components/common";
import { CheckCircle2, XCircle, RefreshCw, Loader2, Search, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { promptDialog } from "@/components/ui/confirm";

export default function Approvals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/approvals/pending");
      setRows(r.data?.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const action = async (taskId, act, label) => {
    const remarks = act !== "approve" ? (await promptDialog({ title: label, message: `Reason for ${label}?`, placeholder: "Enter a reason...", confirmText: label })) || "" : "";
    if (act !== "approve" && !remarks) return;
    try {
      await api.post(`/tasks/${taskId}/${act}`, { remarks });
      toast.success(`${label} done`);
      load();
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };
  const SortTh = ({ label, k }) => (
    <th onClick={() => toggleSort(k)} className="cursor-pointer select-none group">
      <div className="flex items-center gap-1.5">{label}
        {sortKey === k ? (sortDir === "asc" ? <ChevronUp size={11} className="text-[#334E7B]"/> : <ChevronDown size={11} className="text-[#334E7B]"/>)
          : <ChevronsUpDown size={11} className="opacity-0 group-hover:opacity-50 transition-opacity"/>}
      </div>
    </th>
  );

  const displayed = useMemo(() => {
    let data = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(r => Object.values(r).some(v => v && String(v).toLowerCase().includes(q)));
    }
    if (sortKey) {
      data = [...data].sort((a, b) => {
        const va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
        const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return data;
  }, [rows, search, sortKey, sortDir]);

  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approval Inbox" breadcrumbs={["Transactions", "Approvals"]} />

      <div className="erp-card p-3 mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search task code or title..." className="erp-input pl-8 text-sm" />
          {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={12}/></button>}
        </div>
        <span className="text-xs text-slate-500">{displayed.length} results</span>
      </div>

      <div className="erp-card overflow-x-auto">
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div> :
         rows.length === 0 ? <EmptyState title="Inbox clear" hint="No tasks awaiting approval." /> : (
          <table className="erp-table">
            <thead><tr><SortTh label="Task Code" k="task_code"/><SortTh label="Title" k="title"/><SortTh label="Submitted By" k="assigned_to_name"/><th>Level</th><SortTh label="Priority" k="priority_name"/><SortTh label="Status" k="status"/><SortTh label="Updated" k="updated_at"/><th></th></tr></thead>
            <tbody>
              {displayed.map(t => (
                <tr key={t.id} data-testid={`approval-row-${t.task_code}`}>
                  <td className="font-mono text-xs">{t.task_code}</td>
                  <td className="font-medium">{t.title}</td>
                  <td>{t.assigned_to_name || "—"}</td>
                  <td>
                    <span className="badge-base bg-violet-50 text-violet-700 border-violet-200">
                      L{t.current_approval_level || 1}/{t.approval_total_levels || 1}
                    </span>
                  </td>
                  <td><PriorityBadge priority={t.priority_name}/></td>
                  <td><StatusBadge status={t.status}/></td>
                  <td className="text-xs">{fmtDate(t.updated_at)}</td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <button onClick={() => action(t.id, "approve", "Approved")} className="p-1.5 text-emerald-700 hover:bg-emerald-50 rounded-sm" title="Approve" data-testid={`appr-approve-${t.task_code}`}><CheckCircle2 size={14}/></button>
                      <button onClick={() => action(t.id, "rework", "Rework")} className="p-1.5 text-orange-700 hover:bg-orange-50 rounded-sm" title="Rework"><RefreshCw size={14}/></button>
                      <button onClick={() => action(t.id, "reject", "Rejected")} className="p-1.5 text-red-700 hover:bg-red-50 rounded-sm" title="Reject"><XCircle size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
